import React, { useEffect, useState } from "react";
import { Plus, Cpu, Database, Cloud, Activity, Server, MessageSquare, Plug, CheckCircle2, Circle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = { cpu: Cpu, database: Database, cloud: Cloud, activity: Activity, server: Server, "message-square": MessageSquare, plug: Plug };

export default function Integrations() {
  const { t } = useLang();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "" });

  const load = () => api.get("/integrations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name) return;
    try { await api.post("/integrations", form); setOpen(false); setForm({ name: "", category: "" }); load(); toast.success(t("add_integration")); }
    catch { toast.error("Failed"); }
  };

  const getContext = () => ({ total: items.length, connected: items.filter((i) => i.status === "Connected").length,
    names: items.map((i) => i.name) });

  return (
    <div className="space-y-6 fade-up" data-testid="integrations-page">
      <PageHeader title={t("integrations")} subtitle={t("integrations_desc")} screen="integrations" getContext={getContext}>
        {isAdmin && <Button onClick={() => setOpen(true)} data-testid="add-integration-btn" className="rounded-xl gap-2"><Plus className="h-4 w-4" /><span className="hidden sm:inline">{t("add_integration")}</span></Button>}
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => {
          const Icon = ICONS[it.icon] || Plug;
          const connected = it.status === "Connected";
          return (
            <Card key={i} className="p-5 rounded-2xl shadow-sm flex items-start gap-4" data-testid={`integration-${i}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"><Icon className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{it.name}</p>
                <p className="text-xs text-muted-foreground">{it.category}</p>
                <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${connected ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400" : "bg-slate-500/10 border-slate-400/30 text-slate-600 dark:text-slate-400"}`}>
                  {connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                  {connected ? t("connected") : t("not_connected")}
                </span>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-integration-dialog">
          <DialogHeader><DialogTitle>{t("add_integration")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{t("name")}</Label><Input data-testid="integration-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{t("domain")}</Label><Input data-testid="integration-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="LLM Gateway, Vector DB, Webhook..." className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">{t("cancel")}</Button>
            <Button onClick={add} data-testid="save-integration-btn" className="rounded-xl">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
